import React, { useEffect, useMemo, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';

import { useGetAmenities } from '~/hooks/apis/accommodation/useGetAmenities';
import { useSyncRoomAmenities } from '~/hooks/apis/accommodation/useSyncRoomAmenities';

import type { OfferBuilderRoom, RoomAmenity } from '../types';
import { sortRoomAmenityNames } from '../utils';

interface Props {
  onChange: (nextRoomValues: Partial<OfferBuilderRoom>) => void;
  room: OfferBuilderRoom;
}

function getAmenityName(amenity: RoomAmenity) {
  return typeof amenity === 'string' ? amenity : amenity.name;
}

function getAmenityNames(amenities: Array<RoomAmenity> = []) {
  return sortRoomAmenityNames(
    amenities.map(getAmenityName).filter((amenityName): amenityName is string => !!amenityName),
  );
}

export default function AmenitiesTab({ onChange, room }: Props) {
  const [customAmenity, setCustomAmenity] = useState('');
  const autoSyncedRoomKeyRef = useRef<string | undefined>(undefined);
  const { data: amenities = [], isLoading: isLoadingAmenities } = useGetAmenities();
  const selectedAmenityNames = useMemo(() => getAmenityNames(room.amenities), [room.amenities]);
  const selectedAmenityNamesSet = useMemo(() => new Set(selectedAmenityNames), [selectedAmenityNames]);
  const amenityOptions = useMemo(() => {
    const amenityNames = amenities
      .map((amenity) => amenity.name)
      .filter((amenityName): amenityName is string => typeof amenityName === 'string');

    return sortRoomAmenityNames(Array.from(new Set<string>(amenityNames.concat(selectedAmenityNames))));
  }, [amenities, selectedAmenityNames]);
  const syncRoomAmenitiesMutation = useSyncRoomAmenities({
    onSuccess: (amenities) => onChange({ amenities: getAmenityNames(amenities) }),
  });
  const { isPending: isSyncingRoomAmenities, mutate: syncRoomAmenities } = syncRoomAmenitiesMutation;

  const updateAmenity = (amenity: string, checked: boolean) => {
    onChange({
      amenities: checked
        ? sortRoomAmenityNames(Array.from(new Set(selectedAmenityNames.concat(amenity))))
        : selectedAmenityNames.filter((selectedAmenity) => selectedAmenity !== amenity),
    });
  };

  const addCustomAmenity = () => {
    const amenity = customAmenity.trim();

    if (!amenity) {
      return;
    }

    updateAmenity(amenity, true);
    setCustomAmenity('');
  };

  const syncAmenities = () => {
    if (!room.property_id || !room.id) {
      return;
    }

    syncRoomAmenities({
      reservationPropertyId: room.property_id,
      reservationRoomId: room.id,
    });
  };

  useEffect(() => {
    if (!room.property_id || !room.id || isSyncingRoomAmenities) {
      return;
    }

    const roomKey = `${room.property_id}:${room.id}`;

    if (autoSyncedRoomKeyRef.current === roomKey) {
      return;
    }

    autoSyncedRoomKeyRef.current = roomKey;

    if (selectedAmenityNames.length > 0) {
      return;
    }

    syncRoomAmenities({
      reservationPropertyId: room.property_id,
      reservationRoomId: room.id,
      suppressNotifications: true,
    });
  }, [isSyncingRoomAmenities, room.id, room.property_id, selectedAmenityNames.length, syncRoomAmenities]);

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        Toggle the amenities present in this room. Use <Box component="em">Sync</Box> to copy from the property
        defaults.
      </Typography>

      <Box
        sx={{
          columnCount: { xs: 1, sm: 2, lg: 5 },
          columnGap: 3,
        }}
      >
        {isLoadingAmenities ? (
          <Typography color="text.secondary">Loading amenities...</Typography>
        ) : (
          amenityOptions.map((amenity) => (
            <FormControlLabel
              key={amenity}
              control={
                <Checkbox
                  checked={selectedAmenityNamesSet.has(amenity)}
                  onChange={(event) => updateAmenity(amenity, event.target.checked)}
                />
              }
              label={amenity}
              sx={{ breakInside: 'avoid', display: 'flex', mb: 1 }}
            />
          ))
        )}
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField
          size="small"
          label="Custom amenity"
          value={customAmenity}
          onChange={(event) => setCustomAmenity(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addCustomAmenity();
            }
          }}
        />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addCustomAmenity}>
          Add custom amenity
        </Button>
        <Button
          variant="outlined"
          startIcon={<SyncIcon />}
          disabled={!room.property_id || !room.id || isSyncingRoomAmenities}
          onClick={syncAmenities}
        >
          {isSyncingRoomAmenities ? 'Syncing...' : 'Sync room amenities'}
        </Button>
      </Stack>
    </Stack>
  );
}
